import type { ReactNode } from 'react';

import { usePositionedPopup } from '../../hooks/use-positioned-popup/use-positioned-popup';
import { type DropdownItems, Dropdown } from '../dropdown/dropdown-shared';
import { useMultipleDropdown } from '../dropdown/utilities';
import type { FieldContainerBaseProps } from '../field-container/field-container';
import { FieldContainer } from '../field-container/field-container';

interface CollectionProps<Value> {
  defaultChecked?: boolean;
  form: string;
  id: string;
  key?: string;
  name: string;
  value: Value;
}

export interface MultipleDropdownSearchFieldProps<Value = string>
  extends Omit<
    FieldContainerBaseProps,
    'defaultValue' | 'name' | 'readOnly' | 'disabled'
  > {
  defaultValues?: Array<Value>;
  placeholder?: string;
  selectedText: (count: number) => string;
  items: DropdownItems<Value>;
  collection: Array<CollectionProps<Value>>;
  'data-testid'?: string;
  'aria-describedby'?: string;
  size?: 'medium' | 'large';
  noResultsLabel: ReactNode;
  /**
   * Override of logic to show outline error styling on dropdown without validation messages
   */
  showVisualErrorStyles?: boolean;
}

export const MultipleDropdownSearchField = <Value,>({
  items,
  placeholder,
  selectedText,
  label,
  helpText,
  defaultValues,
  'aria-invalid': invalid,
  'aria-describedby': errorMessageId,
  size = 'medium',
  showVisualErrorStyles = false,
  showField = true,
  errors,
  noResultsLabel,
  collection,
}: MultipleDropdownSearchFieldProps<Value>) => {
  'use no memo';
  const {
    menuItems,
    selectedMenuItems,
    isOpen,
    getToggleButtonProps,
    getMenuProps,
    getInputProps,
    highlightedIndex,
    getLabelProps,
    getItemProps,
  } = useMultipleDropdown({
    items,
    defaultValues,
  });

  const { setFloating, setReference, floatingStyles, constrainStyles } =
    usePositionedPopup({
      open: isOpen,
    });

  if (!showField) {
    return null;
  }

  const menuProps = getMenuProps();

  const dropdownItemProps = menuItems.flatMap((menuItem, index) => {
    const collectionProp = collection.find(
      (collectionProp) => collectionProp.value === menuItem.value,
    );

    if (!collectionProp) return [];

    return [
      {
        ...menuItem,
        ...collectionProp,
        value: `${menuItem.value}`,
        key: collectionProp.key || `${collectionProp.value}`,
        defaultChecked: !!selectedMenuItems.find(
          (selectedMenuItem) => selectedMenuItem.value === menuItem.value,
        ),
        checkBoxProps: {
          ...getItemProps({ item: menuItem, index }),
          role: 'checkbox',
        },
      },
    ];
  });

  const hiddenSelectedItems = collection.filter(
    (item) =>
      menuItems.every((itemProps) => itemProps.value !== item.value) &&
      selectedMenuItems.some((selected) => selected.value === item.value),
  );

  const placeholderText =
    selectedMenuItems.length > 0 && selectedText
      ? selectedText(selectedMenuItems.length)
      : placeholder;

  return (
    <FieldContainer
      {...getLabelProps()}
      variant="fieldGroup"
      label={label}
      aria-invalid={invalid}
      errorMessageId={errorMessageId}
      errors={errors}
      helpText={helpText}
    >
      <Dropdown.MultipleSearchInput
        size={size}
        disabled={false} // We might enable this feature in the future
        isOpen={isOpen}
        toggleButtonProps={getToggleButtonProps()}
        getInputProps={getInputProps}
        ref={setReference}
        placeholder={placeholderText}
        showVisualErrorStyles={showVisualErrorStyles}
        aria-invalid={invalid}
        aria-errormessage={errorMessageId} // https://stackoverflow.com/questions/51554395/what-is-the-difference-between-aria-errormessage-and-aria-alert
      />

      {hiddenSelectedItems.length > 0
        ? hiddenSelectedItems.map((item) => (
            <input
              key={item.key || `${item.value}`}
              type="hidden"
              name={item.name}
              value={`${item.value}`}
              defaultChecked={item.defaultChecked}
              form={item.form}
            />
          ))
        : null}

      <Dropdown.Menu
        floatingStyles={floatingStyles}
        setFloating={setFloating}
        disabled={false} // We might enable this feature in the future
        isOpen={isOpen}
        constrainStyles={constrainStyles}
        menuProps={menuProps}
      >
        {dropdownItemProps.length > 0 ? (
          dropdownItemProps.map((item, index) => (
            <Dropdown.MenuItemWithCheckbox
              highlightedIndex={highlightedIndex}
              item={item}
              index={index}
              size={size}
              {...item}
              key={item.key}
              checkBoxProps={item.checkBoxProps}
            />
          ))
        ) : (
          <Dropdown.NoResults>{noResultsLabel}</Dropdown.NoResults>
        )}
      </Dropdown.Menu>
    </FieldContainer>
  );
};
